import * as React from "react";
import {Box, Card, CardContent, Typography} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import EuroIcon from "@mui/icons-material/Euro";
import {useTranslation} from "react-i18next";

interface PayrollRunSummaryMobileProps {
    employeeCount: number;
    totalGross: number;
    totalBankNet: number;
    totalEmployerCost: number;
    formatEUR: (value: number | null | undefined) => string;
}

interface SummaryCardDef {
    icon: React.ReactElement;
    label: string;
    value: string;
}

function SummaryCard({icon, label, value}: SummaryCardDef): React.ReactElement {
    return (
        <Card variant="outlined" sx={{borderRadius: 1}}>
            <CardContent sx={{textAlign: "left"}}>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        backgroundColor: "action.hover",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                    }}
                >
                    {icon}
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                    {label}
                </Typography>
                <Typography variant="h6" fontWeight={700} noWrap>
                    {value}
                </Typography>
            </CardContent>
        </Card>
    );
}

// Condensed 4-card variant of PayrollRunSummary for mobile (see the mobile Payroll Run
// reference) — desktop keeps the full 7-card PayrollRunSummary unchanged.
export default function PayrollRunSummaryMobile({
    employeeCount,
    totalGross,
    totalBankNet,
    totalEmployerCost,
    formatEUR,
}: Readonly<PayrollRunSummaryMobileProps>): React.ReactElement {
    const {t} = useTranslation();

    const cards: SummaryCardDef[] = [
        {icon: <GroupIcon sx={{color: "text.secondary"}} fontSize="small"/>, label: t('payroll.run.summary.employees'), value: String(employeeCount)},
        {icon: <TrendingUpIcon sx={{color: "success.main"}} fontSize="small"/>, label: t('payroll.run.table.header.finalGross'), value: formatEUR(totalGross)},
        {icon: <AccountBalanceIcon sx={{color: "primary.main"}} fontSize="small"/>, label: t('payroll.run.table.header.netToBank'), value: formatEUR(totalBankNet)},
        {icon: <EuroIcon sx={{color: "text.secondary"}} fontSize="small"/>, label: t('payroll.run.summary.totalEmployerCost'), value: formatEUR(totalEmployerCost)},
    ];

    return (
        <Box sx={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 2}}>
            {cards.map((card) => (
                <SummaryCard key={card.label} {...card} />
            ))}
        </Box>
    );
}
